import { ErrorMessage } from "@hookform/error-message";
import { AxiosError } from "axios";
import { type FC, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Trans } from "react-i18next";
import { useBoolean } from "usehooks-ts";

import { getPasswordErrorMessages } from "../../../helpers/password-errors";
import {
  PROFILE_PROPERTIES,
  getUserInitials,
  handleInterfaceLanguageChange,
} from "../../../utils/user-utils";
import { StudentPreview } from "../section/StudentPreview";

import { CloseOverlayButton } from "./CloseOverlayButton";
import { ContentSidebarHeader } from "./ContentSidebarHeader";
import { ContentSidebarMain } from "./ContentSidebarMain";
import { OverlayContainer } from "./OverlayContainer";

import { serverSetPassword } from "api/api-server-actions";
import { LoadingScreen } from "components/LoadingScreen";
import { PasswordForm, PasswordFormValue } from "components/PasswordForm";
import { HoverableEditableText } from "components/hover-widgets/EditableText";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { onLogout } from "components/materials/sidebar/helpers";
import { FormattedValidationError, UserInformationType } from "components/server-types";
import { getSidebarHeader } from "helpers/sidebar";
import { INTERFACE_LANGUAGES, LANGUAGE_LABELS, SupportedLanguage, i18n, t } from "i18n/i18n";
import { PasswordErrorKey } from "i18n/i18next";
import { Avatar } from "mds/components/Avatar";
import { FormField } from "mds/components/FormField";
import { IconText } from "mds/components/IconText";
import { Listbox } from "mds/components/Listbox";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { PersonSingleIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectCurrentUser,
  selectCurrentUserId,
  selectIsStudentPreview,
  selectPresentingSessionIds,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { rollbarAndLogError } from "utils/logger";
import { Link } from "utils/routing";
import { CONTENT_SIDEBAR_TAB_NAME } from "utils/urls";
import { getUserColor } from "worksheets/helpers/colors";

export type SetPasswordFormValue = PasswordFormValue & {
  current_password: string;
};

// TODO: This is somewhat duplicate of the ProfileSettingsMenu, but doesn't share code (when it should)
export const UserProfileSidebarPanel: FC = () => {
  const dispatch = useAppDispatch();
  const { value: formIsShown, setFalse: hideForm, toggle: toggleShowForm } = useBoolean(false);

  const currentUserId = useAppSelector(selectCurrentUserId);
  const user = useObjectSelector(selectCurrentUser, PROFILE_PROPERTIES);
  const presentingSessionIds = useListSelector(selectPresentingSessionIds);
  const { canAuthorSection } = useCapabilities();
  const isStudentPreview = useAppSelector(selectIsStudentPreview);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const showStudentPreviewCard = canAuthorSection || isStudentPreview;

  const form = useForm<SetPasswordFormValue>();
  const currentPassword = form.watch("current_password");
  const onSubmit: SubmitHandler<SetPasswordFormValue> = (formData) => {
    serverSetPassword(formData)
      .then(() => {
        toastSuccessMessage(t("success.toasts.update_success", { item: t("glossary.password") }));
        hideForm();
        form.reset();
      })
      .catch((err: unknown) => {
        if (!(err instanceof AxiosError)) {
          rollbarAndLogError("serverSetPassword", err);
          return;
        }

        const respData = err.response?.data as FormattedValidationError<PasswordErrorKey>;

        if (!respData) {
          rollbarAndLogError("serverSetPassword", err);
          return;
        }

        for (const [field, { details, t: translationKeys }] of Object.entries(respData)) {
          form.setError(field as keyof SetPasswordFormValue, {
            type: "server",
            message: getPasswordErrorMessages(translationKeys ?? [], details),
          });
        }
      });
  };

  useEffect(() => {
    if (currentUserId) {
      storeApi.users.retrieve(currentUserId);
    }
    if (currentUserId && sectionId) {
      storeApi.section_users.list({ user_id: currentUserId, section_id: sectionId });
    }
  }, [currentUserId, sectionId]);

  const onSaveUser = (data: Partial<Pick<UserInformationType, "first_name" | "last_name">>) => {
    if (currentUserId) {
      storeApi.users.partial_update({ id: currentUserId, ...data });
    }
  };

  if (!user || !currentUserId) {
    return <LoadingScreen />;
  }

  // We need to determine how we should handle showing errors and success messages
  // globally though before we spend too much time on this.
  // Also the mechanism for password and confirm password should probably be shared
  // with the signup form. Maybe the hidden lastpass field too.
  return (
    <OverlayContainer>
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <IconText
          iconStart={<PersonSingleIcon className="text-blue" />}
          text={getSidebarHeader(CONTENT_SIDEBAR_TAB_NAME.Profile)}
        />
        <CloseOverlayButton />
      </ContentSidebarHeader>

      <ContentSidebarMain>
        <div className="rounded-lg border border-black-tint-90 bg-white p-2">
          <div className="flex items-center gap-2">
            <Avatar bgColor={getUserColor(user.id)} className="text-white">
              {getUserInitials(user)}
            </Avatar>
            {user.email}
          </div>

          <div className="mt-2">
            <HoverableToolbar reference="first_name" showEditButton>
              <HoverableEditableText
                className="px-2"
                label={t("user_info.first_name")}
                textSize="m"
                value={user.first_name}
                onValueChanged={(value) => onSaveUser({ first_name: value })}
              />
            </HoverableToolbar>
          </div>

          <div>
            <HoverableToolbar reference="last_name" showEditButton>
              <HoverableEditableText
                className="px-2"
                label={t("user_info.last_name")}
                textSize="m"
                value={user.last_name}
                onValueChanged={(value) => onSaveUser({ last_name: value })}
              />
            </HoverableToolbar>
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-black-tint-90 bg-white p-2">
          <div className="flex items-center justify-between">
            <h4 className="h4 my-2 text-black-tint-40">{t("profile_panel.choose_language")}</h4>

            <Listbox
              items={INTERFACE_LANGUAGES.map((lang) => ({
                id: lang,
                title: LANGUAGE_LABELS[lang],
              }))}
              selectedValue={i18n.language as SupportedLanguage}
              onSelectionChange={({ id }) => handleInterfaceLanguageChange(id)}
            />
          </div>
        </div>

        {!formIsShown && (
          <SquareButton
            className="mt-4 w-full max-w-full justify-start"
            eventName="Account settings - Reset password"
            kind="tertiary"
            size="m"
            onClick={toggleShowForm}
          >
            {t("profile_panel.reset_password")}
          </SquareButton>
        )}

        {formIsShown && (
          <div className="mt-4 flex flex-col gap-2 rounded-lg border border-black-tint-90 bg-white p-2">
            <FormProvider {...form}>
              <div className="h4 my-2 text-black-tint-40">{t("profile_panel.reset_password")}</div>
              <form
                onSubmit={form.handleSubmit((data) => {
                  trackEvent("Account settings - Update password", BUTTON_EVENT_CATEGORY);
                  onSubmit(data);
                })}
              >
                <div className="items-first body-s flex flex-col gap-3 pb-2">
                  <ErrorMessage name="root" />

                  <FormField
                    label={t("user_info.current_password")}
                    name="current_password"
                    required
                  >
                    {(controlProps) => (
                      <TextField
                        {...form.register("current_password", {
                          required: t("error.field_required"),
                        })}
                        {...controlProps}
                        autoComplete="current_password"
                        type="password"
                      />
                    )}
                  </FormField>

                  <PasswordForm currentPassword={currentPassword} />

                  <SquareButton kind="accent" size="m" type="submit">
                    {t("user_info.update_password")}
                  </SquareButton>
                </div>
              </form>
            </FormProvider>
          </div>
        )}

        {showStudentPreviewCard && <StudentPreview />}

        <SquareButton
          className="mt-4 w-full max-w-full"
          eventName="Account settings - Logout"
          kind="caution"
          size="m"
          onClick={onLogout(presentingSessionIds, dispatch)}
        >
          {t("profile_panel.logout")}
        </SquareButton>

        <div className="body-xs ml-2 mt-2">
          <Trans i18nKey="user_info.eula_footer">
            <Link target="_blank" to="/terms-of-service-en">
              Terms of Service
            </Link>
            <Link target="_blank" to="/privacy-policy-en">
              Privacy Policy
            </Link>
            <Link target="_blank" to="/third-party-licenses">
              Third-party Licenses
            </Link>
          </Trans>
        </div>
      </ContentSidebarMain>
    </OverlayContainer>
  );
};
